import React, { FC, useEffect, useMemo } from 'react'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import CopyToClipboard from 'react-copy-to-clipboard'
import { Container } from 'typescript-ioc/es5'
import * as randomstring from 'randomstring'
import { DomainDto } from '../../dtos/domain'
import { AppState } from '../../stores/app'
import { AppService, DomainService } from '../../services'
import { simpleFormErrorNotification, simpleNotification } from '../../_utils/utils'
import { getClassNames } from '../../_utils/classnames'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { Form, Select, Skeleton, Tag, notification, Divider, ConfigProvider, Flex, Space, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useForm } from 'antd/es/form/Form'
import { getCollapseKeyTypeLabel } from '@pushly/aqe/lib/utils/notification-collapse-key'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'

interface IDomainServerEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const DomainServerEditor: FC<IDomainServerEditor> = (props) => {
    const appSvc = useService(AppService)
    const domainSvc = useService(DomainService)

    const [form] = useForm<{ collapseKeyType: string }>()
    const initialValues = useMemo(
        () => ({ collapseKeyType: props.domain.collapseKeyType }),
        [props.domain.collapseKeyType],
    )
    const watchedValues = Form.useWatch([], form)
    const watchedCollapseKeyType = watchedValues?.collapseKeyType

    useEffect(() => {
        form.resetFields()
        form.setFieldsValue(initialValues)
    }, [form, initialValues])

    const hasChanges = useMemo(() => {
        return watchedCollapseKeyType !== props.domain.collapseKeyType
    }, [props.domain.collapseKeyType, watchedCollapseKeyType])

    const getCurrentValues = (): Partial<DomainDto> => {
        const values: Partial<DomainDto> = {}
        const formValues = form.getFieldsValue(true)

        if (formValues.collapseKeyType !== props.domain.collapseKeyType) {
            values.collapseKeyType = formValues.collapseKeyType
        }

        return values
    }

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const values = getCurrentValues()

            const { ok, data } = await domainSvc.updateDomainById(props.domain.id, values)

            if (ok) {
                props.onDomainUpdated(data)
                props.onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-server-editor')}
            title="Update Server Keys & Flags"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!hasChanges}
            onClose={props.onClose}
            open={props.visible}
            width={800}
        >
            <Form
                form={form}
                initialValues={initialValues}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
            >
                <Well title="Notification Keys" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        name="collapseKeyType"
                        label="Collapse Key"
                        className={getClassNames('ddf-server-editor-ck')}
                    >
                        <Select size="small">
                            <Select.Option value="DOMAIN_ID">Domain Level</Select.Option>
                            <Select.Option value="NOTIFICATION_SCHEDULE_ID">Notification Level</Select.Option>
                        </Select>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IDomainServerWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDSWState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainServerWell extends React.Component<IDomainServerWell, IDSWState> {
    public state: IDSWState = {
        showDrawer: false,
    }

    protected appState: AppState

    public constructor(props: IDomainServerWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        const currentUserIsDev = this.appState.currentUser?.isInternalDevUser

        return (
            <Well
                className="nested server-well"
                title="Server Keys & Flags"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                footerMode="hidden"
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderNotificationWell()}

                                <Divider className="inner-well-divider" />

                                {currentUserIsDev && this.renderFlagsWell()}
                            </Form>

                            <DomainServerEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.props.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderNotificationWell() {
        return (
            <Well title="Notification Keys" mode="ghost" hideFooter={true} flush>
                <Form.Item label="Collapse Key">
                    <span className={getClassNames('can-copy')}>
                        <CopyToClipboard
                            onCopy={() => this.handleCopied('collapse key')}
                            text={this.domain.collapseKeyType}
                        >
                            <Typography.Text>{getCollapseKeyTypeLabel(this.domain.collapseKeyType)}</Typography.Text>
                        </CopyToClipboard>
                    </span>
                </Form.Item>
            </Well>
        )
    }

    protected renderFlagsWell() {
        return (
            <Well title="Flags" mode="ghost" hideFooter={true} flush>
                <Form.Item
                    label={
                        <Space vertical>
                            <span>Service & Feature Flags</span>
                            <Space align="start">
                                <InfoCircleOutlined className="info-icon" />
                                <Typography.Paragraph
                                    type="secondary"
                                    className="font-size--s"
                                    style={{ marginBottom: 0 }}
                                >
                                    View{' '}
                                    <a
                                        href="https://pushly.atlassian.net/wiki/spaces/AQ/pages/372080658/Flags#Domain-Flags"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Available Tags
                                    </a>
                                </Typography.Paragraph>
                            </Space>
                        </Space>
                    }
                >
                    <Flex wrap gap={4}>
                        {this.domain.flags?.length ? (
                            this.domain.flags.map((flag, idx) => (
                                <Tag variant="outlined" key={idx}>
                                    {flag}
                                </Tag>
                            ))
                        ) : (
                            <Typography.Text type="secondary">No flags</Typography.Text>
                        )}
                    </Flex>
                </Form.Item>
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
